// Lofi Cities Radio (docs/AUDIO_DESIGN.md §13): one station that plays the same song for everyone tuned in. Nothing
// is streamed. planTrack is pure (about 0.01 ms a track), so every browser plans the same hour from the clock and hears
// the same song, bar and note; a listener who tunes in late lands mid-song. Each UTC hour is a show in one style, the
// styles in turn: its first track starts on the hour, and its last ones are picked from seeded stand-ins so the show
// ends just before the next one begins. A track's title comes from its own host city, the same for every listener
// whichever city they watch. The listener's band still applies (it only masks parts, §7), so it stays theirs.
// Listeners vote on the style of the next track (voteOptions, the live worker counts the votes, site/js/live/vote.js):
// a winning style is a pick, and every function here takes the picks (picksOf(h) → Map of slot index → style). A pick
// changes its slot's style and, since styles make tracks of other lengths, the rest of its hour, planned the same way
// again. A slot takes a vote only where every style it offers still lets the hour end on time (the hour's closing two
// never do: they fit it to its end), so a late run of votes can't leave a stretch of silence before the next show.
// Any change to what planTrack draws changes the station too: a tab from before a deploy plays the old one until reload.
import { hash, rng } from '../engine/core.js';
import { planTrack, STYLE_IDS, HANDOFF_BARS } from './arrange.js';
import { styleById } from './styles/index.js';
import { SCENES } from '../scenes/registry.js';
import { CLOSE_LEAD_MS } from '../live/vote.js';

export const HOUR_MS = 3600000;
export const RADIO_ENERGY = 'balanced';
export const END_SLACK_MS = 3000;   // an hour's last track ends at most this long (plus its handoff gap) before the next show
const JOIN_TAIL_MS = 5000;          // tuning in this close to a track's end joins the next one instead
const POOL = 256;                   // seeded tracks an hour's last two come from
const ALTERNATES = 256;             // seeded stand-ins tried for a slot that would leave a stretch nothing fills
const MARGIN_MS = 3000;             // kept inside the pool's shortest and longest, so "fillable" has tracks to fill it
const K_FIRST = 263, K_HOST = 31, K_POOL = 2000, K_ALT = 1000, K_VOTE = 577;
const NONE = new Map(), noPicks = () => NONE;
const seedOf = (a, b) => Math.floor(hash(a | 0, b) * 4294967296) >>> 0;
const mod = (n, m) => ((n % m) + m) % m;

/** The style of the show in UTC hour `h` (hours since 1970): every style in turn, one an hour. */
export const radioStyle = h => STYLE_IDS[mod(h, STYLE_IDS.length)];

/** The three styles a vote for slot `i` of hour `h` offers: the show's first (it wins a tie, and plays when nobody votes),
 *  then two of the others, seeded, so every page offers the same. */
export function voteOptions(h, i) {
  const show = radioStyle(h), others = STYLE_IDS.filter(s => s !== show), r = rng(seedOf(h * 64 + i, K_VOTE));
  for (let j = others.length - 1; j > 0; j--) { const k = Math.floor(r() * (j + 1)); [others[j], others[k]] = [others[k], others[j]]; }
  return [show, others[0], others[1]];
}

/** { style, name, next: { style, name, at } } at epoch ms `ms`: the show on now and the one at the top of the hour. */
export function radioShow(ms) {
  const h = Math.floor(ms / HOUR_MS), show = k => ({ style: radioStyle(k), name: styleById(radioStyle(k)).name });
  return { ...show(h), next: { ...show(h + 1), at: (h + 1) * HOUR_MS } };
}

// A track's title words come from its host city (a stub scene: makeTitle only needs the id and name).
const hostOf = seed => {
  const s = SCENES[Math.floor(hash(seed | 0, K_HOST) * SCENES.length)];
  return { id: s.id, name: s.name };
};
const planOf = (seed, prevKey, style, recent = []) =>
  planTrack(seed, { scene: hostOf(seed), prevKey, recent, vibe: { energy: RADIO_ENERGY, band: 'full', style } });
const lengthOf = p => (p.bars * 240 / p.bpm) * 1000;                    // exactly as the engine times it
const spanOf = p => ((p.bars + HANDOFF_BARS) * 240 / p.bpm) * 1000;    // the track and the handoff gap after it

/**
 * An hour's tail pool: POOL seeded tracks (a track's length depends on its seed and style alone, not on the key or the
 * progressions before it), every sum of two of their spans, sorted, each with the pair that makes it, and the shortest and longest span with
 * MARGIN_MS taken off both. pairFor(x) is the pair that fills x ms to within END_SLACK_MS (the longest such), or null.
 */
const pools = new Map();
function poolOf(h, style) {
  if (pools.has(h)) return pools.get(h);
  if (pools.size > 4) pools.delete(pools.keys().next().value);
  const pool = makePool(h, style);
  pools.set(h, pool);
  return pool;
}
function makePool(h, style) {
  const seeds = Array.from({ length: POOL }, (_, j) => seedOf(h, K_POOL + j));
  const byLength = new Map();                            // one seed per distinct span keeps the sums few
  for (const s of seeds) { const span = spanOf(planOf(s, null, style)); if (!byLength.has(span)) byLength.set(span, s); }
  const spans = [...byLength].sort((a, b) => a[0] - b[0]), sums = [];
  for (let a = 0; a < spans.length; a++) for (let b = a; b < spans.length; b++) sums.push([spans[a][0] + spans[b][0], spans[a][1], spans[b][1]]);
  sums.sort((x, y) => x[0] - y[0]);
  const top = sums.length ? sums[sums.length - 1][0] : 0;
  function pairFor(x) {
    let i = 0, j = sums.length;                          // the last sum <= x (binary search)
    while (i < j) { const m = (i + j) >> 1; if (sums[m][0] <= x) i = m + 1; else j = m; }
    const s = sums[i - 1];
    return s && x - s[0] <= END_SLACK_MS ? [s[1], s[2]] : null;
  }
  const lengths = spans.map(([span]) => span);
  return { pairFor, top, lengths, lo: lengths[0] + MARGIN_MS, hi: lengths[lengths.length - 1] - MARGIN_MS };
}

/**
 * How much of `left` ms (from where the next track would start to the end of the hour) would go quiet, at best: 0 when
 * it's all but used up, or tracks of the style can fill it to within END_SLACK_MS; Infinity past the hour. Up to two
 * tracks' worth, only a pool pair counts (one track alone can't be fitted closely: a style's lengths come in steps of up
 * to 19 s); up to three, a track of one of the pool's lengths and then a pair. Beyond that, n tracks fill anything from
 * n·lo to n·hi, so the most that fit (n = ⌊left / lo⌋) leave the least.
 */
export function quietIn(left, { pairFor, top, lengths, lo, hi }) {
  if (left < 0) return Infinity;
  if (left <= END_SLACK_MS) return 0;
  if (left <= top + END_SLACK_MS) return pairFor(left) ? 0 : left;
  if (left <= top + END_SLACK_MS + lengths[lengths.length - 1]) return lengths.some(v => pairFor(left - v)) ? 0 : left;
  return Math.max(0, left - Math.floor(left / lo) * hi - END_SLACK_MS);
}

/**
 * Hour `h` of the station: { h, style, start, end, slots: [{ h, i, start, end, plan, closing, votable }] } in epoch ms
 * (`end` of a slot is when its track ends; the next one starts HANDOFF_BARS later, like the engine's handoff). The
 * hour's first track starts on the hour. Each next one is the seed chain's (a related key, like the queue) in the
 * show's style, or the picked one (`picks`: slot index → style) where the slot is `votable`, unless that would leave a
 * stretch before the next show that nothing fills (quietIn): then the seeded stand-in in that style that leaves the
 * least (the first that leaves none). A slot is votable when a track of each style voteOptions offers there leaves
 * none. Once a pool pair fills the rest, it ends the hour (both `closing`, never votable). Whether a slot closes or
 * takes a vote never depends on its own pick, only on the ones before it. Planned once per set of picks and kept (a
 * listener needs this hour and the next).
 */
const hours = new Map();
export function radioHour(h, picks = NONE) {
  const key = `${h}|${[...picks].sort((a, b) => a[0] - b[0]).join(';')}`;
  if (hours.has(key)) return hours.get(key);
  const style = radioStyle(h), pool = poolOf(h, style), start = h * HOUR_MS, end = start + HOUR_MS, slots = [];
  let t = start, seed = seedOf(h, K_FIRST), prevKey = null, recent = [];
  const add = (p, closing = false) => {
    slots.push({ h, i: slots.length, start: t, end: t + lengthOf(p), plan: p, closing, votable: false });
    t += spanOf(p);
    seed = p.nextSeed; prevKey = p.endKey; recent = p.recent;
  };
  // this slot's track in style `st`: the chain's, or the stand-in that leaves the least quiet ({ p, quiet })
  const choose = (st, left) => {
    let p = planOf(seed, prevKey, st, recent), quiet = quietIn(left - spanOf(p), pool);
    for (let j = 0; j < ALTERNATES && quiet > 0; j++) {
      const q = planOf(seedOf(seed, K_ALT + j), prevKey, st, recent), qq = quietIn(left - spanOf(q), pool);
      if (qq < quiet) { p = q; quiet = qq; }
    }
    return { p, quiet };
  };
  while (end - t > END_SLACK_MS) {
    const left = end - t, pair = left <= pool.top + END_SLACK_MS ? pool.pairFor(left) : null;
    if (pair) {
      for (const s of pair) add(planOf(s, prevKey, style, recent), true);
      break;
    }
    const i = slots.length, offered = voteOptions(h, i), picked = picks.get(i);
    const votable = offered.every(st => choose(st, left).quiet === 0);
    const { p, quiet } = choose(votable && offered.includes(picked) ? picked : style, left);
    if (quiet === Infinity) break;                       // no track fits in what's left: the city carries it to the hour
    add(p);
    slots[i].votable = votable;
  }
  const hour = { h, style, start, end, slots };
  hours.set(key, hour);
  if (hours.size > 8) hours.delete(hours.keys().next().value);
  return hour;
}

/** The slot after `slot` (the next hour's first after its last one). */
export function slotAfter(slot, picksOf = noPicks) {
  return radioHour(slot.h, picksOf(slot.h)).slots[slot.i + 1] ?? radioHour(slot.h + 1, picksOf(slot.h + 1)).slots[0];
}

/** The slot a listener tuning in at epoch ms `ms` joins: the one on air, or the next if this one has (nearly) ended. */
export function tuneSlot(ms, picksOf = noPicks) {
  const h = Math.floor(ms / HOUR_MS), { slots } = radioHour(h, picksOf(h));
  let s = slots[0];
  for (const x of slots) if (x.start <= ms) s = x;
  return ms < s.end - JOIN_TAIL_MS ? s : slotAfter(s, picksOf);
}

/** The slot `{ h, i }` names (e.g. a plan's `radio` tag), or null for one this hour doesn't have. */
export function slotOf({ h, i }, picksOf = noPicks) {
  return radioHour(h, picksOf(h)).slots[i] ?? null;
}

/** The `n` slots after `slot`, next first. */
export function upcoming(slot, n, picksOf = noPicks) {
  const out = [];
  for (let s = slot; out.length < n;) out.push(s = slotAfter(s, picksOf));
  return out;
}

/**
 * The vote while `slot` plays: { h, i, at, close, options: [{ style, name }] } for the slot after it (`at` its start,
 * `close` when the vote ends, CLOSE_LEAD_MS before), or null when that one takes no vote (one of its hour's closing
 * two, or a slot where some style offered wouldn't let the hour end on time).
 */
export function voteAfter(slot, picksOf = noPicks) {
  const next = slotAfter(slot, picksOf);
  if (!next.votable) return null;
  const options = voteOptions(next.h, next.i).map(style => ({ style, name: styleById(style).name }));
  return { h: next.h, i: next.i, at: next.start, close: next.start - CLOSE_LEAD_MS, options };
}
